import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Tag, Pencil, Trash2 } from "lucide-react";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import { addServiceCategories, deleteCategory, editCategory } from "services/general.service";
import { ServiceCategory } from "types/general.interface";
import useServiceCategories from "hooks/userServiceCategories";
import CategoryForm from "./CategoryForm";

const ServiceCategoryList = () => {
  const { data } = useServiceCategories();
  const [record, setRecord] = useState<ServiceCategory | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const queryClient = useQueryClient();

  const { isLoading: adding, mutate } = useMutation(
    async (input: ServiceCategory) => await addServiceCategories(input),
    {
      onSuccess(data) { toast.success(data?.message); queryClient.invalidateQueries(); toggleAdd(); },
      onError(error: any) { toast.error(error?.response?.data?.message); },
    }
  );

  const { mutate: deleteMutate } = useMutation(
    async (id: string) => await deleteCategory(id),
    {
      onSuccess(data) { toast.success(data?.message); queryClient.invalidateQueries(); toggleDelete(); },
      onError(error: any) { toast.error(error?.response?.data?.message); },
    }
  );

  const { isLoading: editing, mutate: editMutate } = useMutation(
    async (input: ServiceCategory) => await editCategory(record?._id!, input),
    {
      onSuccess(data) { toast.success(data?.message); queryClient.invalidateQueries(); toggleAdd(); },
      onError(error: any) { toast.error(error?.response?.data?.message); },
    }
  );

  const toggleAdd = () => setShowAdd((v) => !v);
  const toggleDelete = () => setShowDelete((v) => !v);

  const handleSubmit = (data: ServiceCategory) => {
    record ? editMutate({ ...data }) : mutate({ ...data });
  };

  const handleDelete = () => { if (record) deleteMutate(record._id!); };

  const onEdit = (cat: ServiceCategory) => { setRecord(cat); setShowAdd(true); };
  const handleAdd = () => { setRecord(null); setShowAdd(true); };

  return (
    <div>
      <BreadCrumb title="Service Categories" pageTitle="Service Categories" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-gray-500">{data?.data?.length ?? 0} categories</p>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Plus size={16} /> Add Category
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {(data?.data?.length ?? 0) === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Tag size={22} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No service categories yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-stack className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.data?.map((cat) => (
                  <tr key={cat._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Name" data-cell="title" className="px-4 py-3 font-medium text-gray-900">{cat.name}</td>
                    <td data-label="Description" className="px-4 py-3 text-gray-500">{cat.description}</td>
                    <td data-label="Actions" data-cell="actions" className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onEdit(cat)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                          <Pencil size={13} /> Edit
                        </button>
                        <button
                          onClick={() => { setRecord(cat); toggleDelete(); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={showAdd}
        onHide={toggleAdd}
        id="catModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">
            {record ? "Edit Category" : "Add Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-5 py-4 overflow-y-auto">
          <CategoryForm onCancel={toggleAdd} onSubmit={handleSubmit} isLoading={adding || editing} data={record} />
        </Modal.Body>
      </Modal>

      <DeleteModal show={showDelete} onHide={toggleDelete} onDelete={handleDelete} />
    </div>
  );
};

export default ServiceCategoryList;
